<template>
  <div
    :class="[$style.base, { [$style.smallScreen]: isSmallScreen }]"
    v-test="'package-employees'"
  >
    <BaseInputLabel
      :text="$t('appointment.create.employee_per_service')"
      required
    />
    <div :class="$style.rows">
      <div
        v-for="(part, index) in parts"
        :key="partIndexes[index]"
        :class="$style.row"
        v-test="'package-employees-row'"
      >
        <div :class="$style.service">
          <BaseText>{{ serviceName(part) }}</BaseText>
        </div>
        <PartDuration
          :class="[$style.duration, { [$style.withProcessingTime]: !!serviceOf(part)?.requiresProcessingTime }]"
          :part="part"
          :requiresProcessingTime="!!serviceOf(part)?.requiresProcessingTime"
          :disabled="onlyResource"
          v-test="'package-part-duration'"
        />
        <PartResources
          :part="part"
          :partIndex="partIndexes[index]"
          hideEmployeeLabel
        />
      </div>
    </div>
  </div>
</template>

<script
  lang="ts"
  setup
>
import { storeToRefs } from 'pinia';
import { isSmallScreen } from '@/modules/calendar/appointment/helpers.ts';
import { useCreateAppointmentStore } from '@/stores/calendar-create-appointment';
import { useResourcesStore } from '@/stores/resources';
import type { AppointmentPartAttributes } from '@/types';
import { usePackageParts } from '../../part-rows';
import PartDuration from './PartDuration.vue';
import PartResources from './PartResources.vue';

const props = withDefaults(
  defineProps<{
    partIndexes: number[];
    onlyResource?: boolean;
  }>(),
  {
    onlyResource: false,
  },
);

const { services } = storeToRefs(useCreateAppointmentStore());
const { parts } = usePackageParts(() => props.partIndexes);

const serviceOf = (part: AppointmentPartAttributes) =>
  services.value.find((service) => service.id === part.serviceId);

const serviceName = (part: AppointmentPartAttributes) => serviceOf(part)?.name;
</script>

<style
  lang="scss"
  module
>
.base {
  width: 100%;
}

.rows {
  display: flex;
  flex-direction: column;
  gap: $spacing * 0.5;
}

.row {
  width: 100%;
  display: flex;
  align-items: center;
  gap: $spacing * 0.5;
  padding: $spacing * 0.5;
  border: 1px solid var(--color-border);
  border-radius: $radius;
  flex-wrap: wrap;

  .base.smallScreen & {
    flex-direction: column;
    align-items: stretch;
  }
}

.service {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  flex-grow: 1;
}

.duration {
  &.withProcessingTime {
    width: 30%;
  }

  &:not(.withProcessingTime) {
    width: 25%;
  }

  .base.smallScreen & {
    width: 100%;
  }
}
</style>
